import React, { useRef, useState } from 'react';
import styled from 'styled-components';
import { MdEdit } from 'react-icons/md';
import { LuTrash2 } from 'react-icons/lu';
import { addPopup } from '../../supplementary_components/store';
import { Form } from '../../general_components/forms/Form';
import { AddButton } from '../../general_components/content_management/reusableItems/buttons';
import { OOS_OPTIONS, labelFor, violationSuggestionLabel } from './safetyApi';

const Section = styled.div`
    border: 1px solid var(--medium-lighter-gray, var(--extra-light-gray));
    border-radius: 0.4rem;
    padding: 0.75rem 0.75rem 0.45rem;
    margin-bottom: 0.35rem;
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
`;

const Entry = styled.div`
    display: flex;
    align-items: center;
    gap: 0.75rem;
    color: var(--dark-gray);
    font-weight: 600;
    padding: 0.65rem 0.75rem;
    border: none;
    border-radius: 0.4rem;
    background: var(--extra-lighter-gray);

    span {
        flex: 1;
    }

    button {
        display: inline-flex;
        align-items: center;
        background: none;
        border: none;
        color: var(--gray);
        cursor: pointer;
        padding: 0;
        font-size: 1.15rem;

        &:hover {
            color: var(--main-color);
        }
    }
`;

export function SafetyEntriesField({ column }) {
    const [entries, setEntries] = useState(column.entries || []);
    const changeMarker = useRef(null);

    const update = (next, previous) => {
        column.entries = next;
        setEntries(next);
        column.onEntriesChange?.(next, previous);
        // Description is not shown on the entry row, so a description-only edit
        // does not change the form DOM. Tell the parent form it has a change.
        changeMarker.current?.dispatchEvent(new Event('input', { bubbles: true }));
    };

    const openForm = (entry, index) => {
        addPopup({
            content: Form,
            EZCloseAllowed: false,
            popupData: {
                configuration: column.buildForm(entry, (saved) => {
                    const previous = column.entries || [];
                    const next = entry == null
                        ? [...previous, saved]
                        : previous.map((item, itemIndex) => (itemIndex === index ? saved : item));
                    update(next, previous);
                }),
            },
        });
    };

    return (
        <Section id={column.name}>
            <input
                ref={changeMarker}
                type="text"
                defaultValue="1"
                readOnly
                tabIndex={-1}
                aria-hidden="true"
                className="hiddenField"
                style={{ position: 'absolute', width: 0, height: 0, padding: 0, border: 0, opacity: 0 }}
            />
            {entries.map((entry, index) => (
                <Entry key={entry.id || `${column.summary(entry)}-${index}`}>
                    <span>
                        {column.summary(entry)}
                        {entry.outOfService && entry.outOfService !== 'NONE'
                            ? ` · ${labelFor(OOS_OPTIONS, entry.outOfService)}`
                            : ''}
                    </span>
                    <button type="button" aria-label="Edit" onClick={() => openForm(entry, index)}><MdEdit /></button>
                    <button type="button" aria-label="Remove" onClick={() => update(entries.filter((_, itemIndex) => itemIndex !== index), entries)}><LuTrash2 /></button>
                </Entry>
            ))}
            <div className="mainAddButton removePadding" style={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
                <AddButton action={() => openForm(null, -1)} customSize="1.5rem" />
            </div>
        </Section>
    );
}

export function violationSummary(entry) {
    return violationSuggestionLabel({
        code: entry.code,
        description: entry.officialDescription || entry.description,
        officialDescription: entry.officialDescription,
        violationGroup: entry.violationGroup,
    });
}
